import { CoreError } from '../errors'
import {
  BS_EPOCH_AD,
  BS_FIRST_YEAR,
  BS_LAST_YEAR,
  BS_MONTH_DAYS,
  BS_PROVISIONAL_FROM,
} from './bs-data'

/** A calendar date without time or timezone. month is 1–12. */
export interface CalendarDate {
  year: number
  month: number
  day: number
}

export const BS_MONTHS_EN = [
  'Baisakh',
  'Jestha',
  'Asar',
  'Shrawan',
  'Bhadra',
  'Asoj',
  'Kartik',
  'Mangsir',
  'Poush',
  'Magh',
  'Falgun',
  'Chaitra',
] as const

export const BS_MONTHS_NE = [
  'बैशाख',
  'जेठ',
  'असार',
  'साउन',
  'भदौ',
  'असोज',
  'कार्तिक',
  'मंसिर',
  'पुस',
  'माघ',
  'फागुन',
  'चैत',
] as const

const MS_PER_DAY = 86_400_000
const EPOCH_UTC = Date.UTC(BS_EPOCH_AD.year, BS_EPOCH_AD.month - 1, BS_EPOCH_AD.day)

// Days from 1 Baisakh BS_FIRST_YEAR to 1 Baisakh of each year.
const YEAR_START_OFFSET: number[] = [0]
for (const months of BS_MONTH_DAYS) {
  YEAR_START_OFFSET.push((YEAR_START_OFFSET.at(-1) ?? 0) + months.reduce((a, d) => a + d, 0))
}

export function parseIsoDate(iso: string): CalendarDate {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) throw new CoreError('invalid_date', `"${iso}" is not a YYYY-MM-DD date`)
  const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
  const check = new Date(Date.UTC(date.year, date.month - 1, date.day))
  if (
    check.getUTCFullYear() !== date.year ||
    check.getUTCMonth() !== date.month - 1 ||
    check.getUTCDate() !== date.day
  ) {
    throw new CoreError('invalid_date', `"${iso}" is not a real date`)
  }
  return date
}

export function formatIsoDate(date: CalendarDate): string {
  const pad = (n: number, w: number) => n.toString().padStart(w, '0')
  return `${pad(date.year, 4)}-${pad(date.month, 2)}-${pad(date.day, 2)}`
}

export function daysInBsMonth(year: number, month: number): number {
  const days = BS_MONTH_DAYS[year - BS_FIRST_YEAR]?.[month - 1]
  if (days === undefined) {
    throw new CoreError(
      'date_out_of_range',
      `BS ${year}-${month} is outside ${BS_FIRST_YEAR}–${BS_LAST_YEAR}`,
    )
  }
  return days
}

/** True when the year's month lengths are projections, not the published calendar. */
export function isProvisionalBsYear(year: number): boolean {
  return year >= BS_PROVISIONAL_FROM
}

export function isValidBsDate(date: CalendarDate): boolean {
  if (date.year < BS_FIRST_YEAR || date.year > BS_LAST_YEAR || date.month < 1 || date.month > 12)
    return false
  return (
    Number.isInteger(date.day) && date.day >= 1 && date.day <= daysInBsMonth(date.year, date.month)
  )
}

export function adToBs(ad: CalendarDate | string): CalendarDate {
  const date = typeof ad === 'string' ? parseIsoDate(ad) : ad
  let remaining = Math.round(
    (Date.UTC(date.year, date.month - 1, date.day) - EPOCH_UTC) / MS_PER_DAY,
  )
  if (remaining < 0 || remaining >= (YEAR_START_OFFSET.at(-1) ?? 0)) {
    throw new CoreError(
      'date_out_of_range',
      `${formatIsoDate(date)} is outside the supported BS range`,
    )
  }
  let yearIndex = 0
  while ((YEAR_START_OFFSET[yearIndex + 1] ?? Infinity) <= remaining) yearIndex++
  remaining -= YEAR_START_OFFSET[yearIndex] ?? 0
  const months = BS_MONTH_DAYS[yearIndex] ?? []
  let month = 0
  while (remaining >= (months[month] ?? Infinity)) {
    remaining -= months[month] ?? 0
    month++
  }
  return { year: BS_FIRST_YEAR + yearIndex, month: month + 1, day: remaining + 1 }
}

export function bsToAd(bs: CalendarDate): CalendarDate {
  if (!isValidBsDate(bs)) {
    throw new CoreError('invalid_date', `BS ${bs.year}-${bs.month}-${bs.day} is not a valid date`)
  }
  const months = BS_MONTH_DAYS[bs.year - BS_FIRST_YEAR] ?? []
  let offset = YEAR_START_OFFSET[bs.year - BS_FIRST_YEAR] ?? 0
  for (let m = 0; m < bs.month - 1; m++) offset += months[m] ?? 0
  offset += bs.day - 1
  const d = new Date(EPOCH_UTC + offset * MS_PER_DAY)
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() }
}

/** "2082-04-01" or, with names, "1 Shrawan 2082" / "१ साउन २०८२". */
export function formatBsDate(bs: CalendarDate, style: 'iso' | 'en' | 'ne' = 'iso'): string {
  if (style === 'iso') return formatIsoDate(bs)
  if (style === 'en') return `${bs.day} ${BS_MONTHS_EN[bs.month - 1]} ${bs.year}`
  const toNe = (n: number) => n.toString().replace(/\d/g, (d) => '०१२३४५६७८९'[Number(d)] ?? d)
  return `${toNe(bs.day)} ${BS_MONTHS_NE[bs.month - 1]} ${toNe(bs.year)}`
}
